import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useToast } from "../App";
import PipelineNav from "../components/PipelineNav";
import { api } from "../lib/api";
import { CARD_TYPE_LABEL, bytes } from "../lib/format";
import type { ExportRecord, Preflight, ProjectDetail } from "../lib/types";

export default function ExportPage() {
  const { projectId = "" } = useParams();
  const { pushError, push } = useToast();

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [preflight, setPreflight] = useState<Preflight | null>(null);
  const [exports, setExports] = useState<ExportRecord[]>([]);
  const [deckName, setDeckName] = useState("");
  const [subdecks, setSubdecks] = useState(true);
  const [extraTags, setExtraTags] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, flight, history] = await Promise.all([
        api.getProject(projectId),
        api.preflight(projectId),
        api.listExports(projectId),
      ]);
      setProject(detail);
      setPreflight(flight);
      setExports(history);
      setDeckName((current) => current || detail.deck_name || detail.name);
      setSubdecks(detail.options?.organise_into_subdecks ?? true);
    } catch (error) {
      pushError(error);
    }
  }, [projectId, pushError]);

  useEffect(() => {
    load();
  }, [load]);

  const runExport = async () => {
    setBusy(true);
    try {
      const record = await api.exportDeck(projectId, {
        deck_name: deckName.trim() || undefined,
        include_statuses: ["accepted"],
        organise_into_subdecks: subdecks,
        extra_tags: extraTags.split(/\s+/).filter(Boolean),
      });
      push(`Built ${record.filename} — ${record.card_count} cards.`, "good");
      // Trigger the browser download immediately; the file is the deliverable.
      window.location.href = record.download_url;
      await load();
    } catch (error) {
      pushError(error);
    } finally {
      setBusy(false);
    }
  };

  if (!project || !preflight)
    return <main className="page"><p className="muted">Loading…</p></main>;

  const nothingAccepted = preflight.accepted === 0;

  return (
    <main className="page page-narrow">
      <PipelineNav projectId={projectId} projectName={project.name} current="export" />

      <div className="page-head">
        <div className="grow">
          <h1>Export to Anki</h1>
          <p>Builds a real .apkg file you can import straight into Anki.</p>
        </div>
      </div>

      {nothingAccepted ? (
        <div className="empty">
          <h3>No cards accepted yet</h3>
          <p>Only accepted cards are exported. Review your cards first.</p>
          <Link to={`/projects/${projectId}/review`} className="btn btn-primary">
            Review cards
          </Link>
        </div>
      ) : (
        <div className="stack">
          <div className="panel">
            <h2 style={{ marginBottom: 12 }}>What you'll get</h2>
            <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))" }}>
              <Stat value={preflight.exportable_notes} label="Notes" />
              <Stat value={preflight.anki_cards} label="Anki cards" />
              <Stat value={preflight.media_referenced} label="Media files" />
            </div>

            <div className="row row-wrap" style={{ marginTop: 12, gap: 6 }}>
              {Object.entries(preflight.by_type).map(([type, count]) => (
                <span key={type} className="chip">
                  {count} × {CARD_TYPE_LABEL[type] ?? type}
                </span>
              ))}
            </div>

            {preflight.anki_cards !== preflight.exportable_notes && (
              <p className="small faint" style={{ marginTop: 10, marginBottom: 0 }}>
                More cards than notes because reversed, cloze, and occlusion notes each generate
                several cards in Anki.
              </p>
            )}
          </div>

          {preflight.blocked.length > 0 && (
            <div className="alert alert-warn">
              <span>⚠</span>
              <div>
                <strong>
                  {preflight.blocked.length} accepted card
                  {preflight.blocked.length === 1 ? "" : "s"} can't be exported
                </strong>
                <div className="alert-hint">
                  {preflight.blocked[0].errors[0]?.message}
                  {preflight.blocked.length > 1 && " (and others)"} — they'll be skipped.{" "}
                  <Link to={`/projects/${projectId}/review`}>Fix them in review</Link>.
                </div>
              </div>
            </div>
          )}

          {preflight.requires_anki_2310 && (
            <div className="alert alert-info">
              <span>ℹ</span>
              <div>
                <strong>This deck contains image occlusion cards</strong>
                <div className="alert-hint">
                  They need Anki 23.10 or newer to render. Anki will import them under a notetype
                  called "Image Occlusion+" — that's normal, it's how Anki avoids overwriting its
                  built-in one.
                </div>
              </div>
            </div>
          )}

          <div className="panel">
            <h2 style={{ marginBottom: 12 }}>Deck options</h2>
            <label className="field">
              <span className="label">Deck name</span>
              <input type="text" value={deckName} onChange={(e) => setDeckName(e.target.value)} />
              <span className="hint">
                Use <code className="mono">::</code> for nesting, e.g.{" "}
                <code className="mono">University::Biology</code>.
              </span>
            </label>

            <label className="checkline" style={{ marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={subdecks}
                onChange={(e) => setSubdecks(e.target.checked)}
              />
              <span>Organise into subdecks by topic</span>
            </label>

            <label className="field">
              <span className="label">Extra tags (space separated, optional)</span>
              <input
                type="text"
                value={extraTags}
                placeholder="exam-1 semester-2"
                onChange={(e) => setExtraTags(e.target.value)}
              />
            </label>

            <button className="btn-primary btn-lg" onClick={runExport} disabled={busy}>
              {busy ? "Building deck…" : "Build and download .apkg"}
            </button>
          </div>

          <div className="panel">
            <h3 style={{ marginBottom: 8 }}>Importing into Anki</h3>
            <ol className="small muted" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
              <li>Open Anki.</li>
              <li>File → Import, and choose the downloaded .apkg file.</li>
              <li>
                Re-exporting later updates the same notes rather than duplicating them, because card
                identifiers stay stable.
              </li>
            </ol>
          </div>

          {exports.length > 0 && (
            <div className="panel">
              <h3 style={{ marginBottom: 10 }}>Previous exports</h3>
              <div className="stack" style={{ gap: 6 }}>
                {exports.map((record) => (
                  <div key={record.id} className="file-row">
                    <span className="file-icon">APKG</span>
                    <div className="grow" style={{ minWidth: 0 }}>
                      <div className="mono" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        {record.filename}
                      </div>
                      <div className="small faint">
                        {record.note_count} notes · {record.card_count} cards ·{" "}
                        {record.media_count} media · {bytes(record.size_bytes)} ·{" "}
                        {new Date(record.created_at).toLocaleString()}
                      </div>
                    </div>
                    <a className="btn btn-sm" href={record.download_url}>
                      Download
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </main>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="stat">
      <span className="value">{value}</span>
      <span className="label">{label}</span>
    </div>
  );
}
